import _ from 'lodash'
import { buildTimerEvents } from '../../../shared/util/timer/timerEvents'
import timerStates from '../../../shared/util/timer/timerStates'

/*
  This Timer object is a wrapper around UserTasks. The idea is that any part of
  UI which used to call _getUserTask and alter the model directly, will now get
  one of these instead. Then, we go through, find all the calculations and
  logic that were done to the naked objects, and move them here instead. Finally,
  we can remove the direct property access.

  The associated objects TimeCard and Task still expose their naked database properties to the frontend.
  Currently, some places in the frontend still use these, but in the future we'd prefer adding properties
  related to the timer on this object as methods, instead of using the associated objects.
*/

const _buildTimerFromUserTask = userTask => {
  return {
    exists: true,
    calculateTimeSpent,
    doesRelateToTimeCardId,
    doesRelateToTaskId,
    isAssignedToProjectActivity,
    isStarted,
    isPaused,
    isStartable,
    isPauseable,
    isResumable,
    isStoppable,
    resume,
    pause,
    stop,
    update,
    ID: () => userTask.id,
    ActivityId: () => userTask.activityId,
    ProjectId: () => userTask.projectId,
    TimeCard: () => _.cloneDeep(userTask.timeCard),
    TimeCardId: () => userTask.timeCardId,
    TimeInSeconds: () => userTask.timeInSeconds,
    Task: () => _.cloneDeep(userTask.task),
    TaskId: () => userTask.taskId,
    StartedOn: () => userTask.startedOn,
    Status: () => userTask.status
  }
}

function calculateTimeSpent (timeTaken) {
  if (!this.isStarted()) {
    return timeTaken
  } else if (this.Task()) {
    return this.Task().timeSpent || 0
  } else if (this.TimeCard()) {
    return this.TimeCard().timeTaken || 0
  } else {
    return timeTaken
  }
}

function doesRelateToTimeCardId (timeCardId) {
  return _doesFieldRelateToObject(this.TimeCardId(), timeCardId)
}

function doesRelateToTaskId (taskId) {
  return _doesFieldRelateToObject(this.TaskId(), taskId)
}

const _doesFieldRelateToObject = (field, obj) => Boolean(field && obj && field === obj)

function isAssignedToProjectActivity () {
  return Boolean(this.ProjectId() && this.ActivityId())
}

function isStarted () { return this.Status() === timerStates.STARTED }
function isPaused () { return this.Status() === timerStates.PAUSED }
function isStartable () { return false }
function isPauseable () { return this.isStarted() }
function isResumable () { return this.isPaused() }
function isStoppable () { return [timerStates.PAUSED, timerStates.STARTED].includes(this.Status()) }

function resume (app, timerEvents = buildTimerEvents(app)) { return timerEvents.resume(this.ID()) }
function pause (app, timerEvents = buildTimerEvents(app)) { return timerEvents.pause(this.ID()) }
function stop (app, timerEvents = buildTimerEvents(app)) { return timerEvents.stop(this.ID()) }

function update (app, updateObj, timerEvents = buildTimerEvents(app)) {
  return timerEvents.update(this.ID(), updateObj)
}

/* buildTimer takes either a userTask object, or null. Either way, you get back
a valid object that you can call. */
const buildTimer = userTask => {
  return userTask ? _buildTimerFromUserTask(userTask) : _buildNullTimer()
}

/* This is basically the Null Object Pattern in action.
https://en.wikipedia.org/wiki/Null_object_pattern

This way, clients of this module don't have to check for null - we do the null
check once in buildTimer, and the Null Timer object handles sensible default
values.
*/
const _buildNullTimer = () => ({
  exists: false,
  calculateTimeSpent: () => 0,
  doesRelateToTimeCardId: () => false,
  doesRelateToTaskId: () => false,
  isAssignedToProjectActivity: () => false,
  isStarted: () => false,
  isPaused: () => false,
  isStartable: () => true,
  isPauseable: () => false,
  isResumable: () => false,
  isStoppable: () => false,
  resume: () => null,
  pause: () => null,
  stop: () => null,
  update: () => null,
  ID: () => null,
  ActivityId: () => null,
  ProjectId: () => null,
  TimeCard: () => null,
  TimeCardId: () => null,
  TimeInSeconds: () => 0,
  Task: () => null,
  TaskId: () => null,
  StartedOn: () => null,
  Status: () => timerStates.NONE
})

export { buildTimer }
